import { useState, useEffect, useCallback, useRef } from "react";
import { getFuturesContractSize } from "../shared/getContractSize";

/**
 * MEXC Futures (USDT perp)
 * WS: wss://contract.mexc.com/edge
 * symbol format: ETH_USDT
 */
export const useMexcFutures = (symbol) => {
  const isMounted = useRef(true);

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError]             = useState(null);
  const [tickerData, setTickerData]   = useState(null);
  const [orderBook, setOrderBook]     = useState({ asks: [], bids: [] });
  const [fundingRate, setFundingRate] = useState(0);

  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const orderBookRef = useRef({ asks: [], bids: [] });
  const animationFrameRef = useRef(null);
  const hasFullOrderBook = useRef(false);
  const contractSizeRef  = useRef(1);   // 👈 множитель (contractSize)

  const scaleBook = useCallback((ob) => {
    const k = contractSizeRef.current || 1;
    const mul = (arr) => arr.map(([p, q]) => [p, String(Number(q) * k)]);
    return { asks: mul(ob.asks || []), bids: mul(ob.bids || []) };
  }, []);

  const formatSymbol = useCallback((sym) => `${sym.replace(/_USDT$/, "")}_USDT`, []);

  const fetchInitialData = useCallback(async () => {
    // если понадобится — сюда можно добавить REST-тикер/фандинг
  }, []);

  const closeConnection = useCallback(() => {
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) wsRef.current.close();
      wsRef.current = null;
    }
    if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
    if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
    setIsConnected(false);
    setError(null);
    setTickerData(null);
    setOrderBook({ asks: [], bids: [] });
    setFundingRate(0);
  }, []);

  const subscribeToFullOrderBook = useCallback((ws, formattedSymbol) => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ method: "sub.depth.full", param: { symbol: formattedSymbol, limit: 20 } }));
    }
  }, []);

  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      if (!isMounted.current) return;

      if (data.channel?.startsWith("rs.") || data.channel === "pong") return;

      if (data.channel === "push.ticker") {
        const t = {
          symbol: data.data.symbol,
          lastPrice: data.data.last || data.data.lastPrice,
          volume24: data.data.volume24 || data.data.volume,
          fundingRate: data.data.fundingRate,
        };
        setTickerData((prev) => ({ ...(prev || {}), ...t }));
        if (data.data.fundingRate !== undefined) setFundingRate(data.data.fundingRate);
      }

      if (data.channel === "push.depth.full" && data.data) {
        const asks = [...(data.data.asks || [])].sort((a,b)=>+a[0]-+b[0]).slice(0,20);
        const bids = [...(data.data.bids || [])].sort((a,b)=>+b[0]-+a[0]).slice(0,20);
        orderBookRef.current = { asks, bids };
        hasFullOrderBook.current = true;
        setOrderBook(scaleBook(orderBookRef.current)); // 👈 масштабируем в монеты
        return;
      }

      if (data.channel === "push.depth" && data.data && hasFullOrderBook.current) {
        const cur = orderBookRef.current;
        const nextAsks = [...cur.asks];
        const nextBids = [...cur.bids];

        if (data.data.asks) {
          data.data.asks.forEach(([p,q]) => {
            const pn = +p, i = nextAsks.findIndex(([pp]) => +pp === pn);
            if (+q === 0) { if (i !== -1) nextAsks.splice(i,1); }
            else { if (i !== -1) nextAsks[i] = [p,q]; else nextAsks.push([p,q]); }
          });
        }
        if (data.data.bids) {
          data.data.bids.forEach(([p,q]) => {
            const pn = +p, i = nextBids.findIndex(([pp]) => +pp === pn);
            if (+q === 0) { if (i !== -1) nextBids.splice(i,1); }
            else { if (i !== -1) nextBids[i] = [p,q]; else nextBids.push([p,q]); }
          });
        }

        orderBookRef.current = {
          asks: nextAsks.sort((a,b)=>+a[0]-+b[0]).slice(0,20),
          bids: nextBids.sort((a,b)=>+b[0]-+a[0]).slice(0,20),
        };

        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = requestAnimationFrame(() => {
          setOrderBook(scaleBook(orderBookRef.current)); // 👈 масштабируем перед set
        });
      }

      if (data.channel === "push.funding.rate") {
        setFundingRate(data.data.rate || 0);
      }
    } catch (e) {
      if (!isMounted.current) return;
      console.error(`❌ MEXC FUTURES: ошибка обработки сообщения:`, e);
    }
  }, [scaleBook]);

  useEffect(() => {
    isMounted.current = true;
    hasFullOrderBook.current = false;

    setTickerData(null);
    setOrderBook({ asks: [], bids: [] });
    setFundingRate(0);
    orderBookRef.current = { asks: [], bids: [] };

    closeConnection();
    if (!symbol || symbol.length < 2) { setError("Invalid symbol"); return; }

    const formattedSymbol = formatSymbol(symbol);
    console.log(`🔄 MEXC FUTURES: подключение для ${formattedSymbol}`);

    (async () => {
      // загрузим размер контракта ДО подписок
      contractSizeRef.current = await getFuturesContractSize("mexc", formattedSymbol);
      fetchInitialData();
      const ws = new WebSocket("wss://contract.mexc.com/edge");
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isMounted.current) return;
        console.log(`🔗 MEXC FUTURES: WebSocket подключен для ${formattedSymbol}`);
        setIsConnected(true);
        setError(null);

        subscribeToFullOrderBook(ws, formattedSymbol);

        [
          { method: "sub.ticker",       param: { symbol: formattedSymbol } },
          { method: "sub.depth",        param: { symbol: formattedSymbol } },
          { method: "sub.funding.rate", param: { symbol: formattedSymbol } },
        ].forEach((sub) => ws.send(JSON.stringify(sub)));

        pingIntervalRef.current = setInterval(() => {
          try { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ method: "ping" })); } catch {}
        }, 15000);
      };

      ws.onmessage = handleMessage;
      ws.onerror = (e) => { console.error("❌ MEXC FUTURES ws:", e); setError(e?.message || "WS error"); setIsConnected(false); };
      ws.onclose  = ()   => { console.log(`🔌 MEXC FUTURES: WS закрыт для ${formattedSymbol}`); setIsConnected(false); };
    })();

    return () => { isMounted.current = false; closeConnection(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  return { isConnected, error, tickerData, orderBook, fundingRate };
};
